import { useEffect, useState } from 'react';
import { useUserData } from '../components/UserDataContext';
import { PresenceService, activityLabel, RECENTLY_ACTIVE_WINDOW_MS } from '../services/PresenceService';

/** The signed-in user's own "Show my activity status" setting, kept in sync. */
export function useMyActivityVisibility(): boolean {
  const [visible, setVisible] = useState(PresenceService.getMyVisibility());
  useEffect(() => {
    setVisible(PresenceService.getMyVisibility());
    return PresenceService.subscribeMyVisibility(setVisible);
  }, []);
  return visible;
}

/**
 * Another user's activity status as a display label.
 *
 * Returns nothing for yourself, when your own status is off (the reciprocal
 * hide), and for anyone blocked in either direction. Those checks mirror the
 * rules so a read that would be denied is never attempted; the rules are still
 * what actually enforces them.
 *
 * `live` subscribes, for the single person on screen. Without it, the read is a
 * cached one-shot, re-checked every minute, which is what list rows want.
 */
export function useActivityStatus(
  userId?: string | number | null,
  options: { live?: boolean } = {},
): { label: string | null; isActiveNow: boolean; isRecentlyActive: boolean } {
  const { userProfile, blockedUsers, blockedBy } = useUserData();
  const myVisible = useMyActivityVisibility();
  const uid = userId ? String(userId) : '';
  const me = String(userProfile?.id || '');
  const blocked = !!uid && ((blockedUsers || []).includes(uid) || (blockedBy || []).includes(uid));
  const enabled = !!uid && !!me && uid !== me && myVisible && !blocked;
  const live = !!options.live;

  const [lastActiveMs, setLastActiveMs] = useState<number | null>(null);
  // Bumped on a timer so "Active now" turns into "Active 3m ago" on its own.
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) { setLastActiveMs(null); return; }
    let cancelled = false;

    if (live) {
      const stop = PresenceService.watchLastActive(uid, ms => { if (!cancelled) setLastActiveMs(ms); });
      const timer = setInterval(() => setTick(n => n + 1), 30_000);
      return () => { cancelled = true; stop(); clearInterval(timer); };
    }

    const load = () => PresenceService.getLastActive(uid).then(ms => { if (!cancelled) setLastActiveMs(ms); });
    void load();
    const timer = setInterval(() => { setTick(n => n + 1); void load(); }, 60_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [uid, enabled, live]);

  if (!enabled) return { label: null, isActiveNow: false, isRecentlyActive: false };
  const label = activityLabel(lastActiveMs);
  const isRecentlyActive =
    lastActiveMs != null && Math.max(0, Date.now() - lastActiveMs) < RECENTLY_ACTIVE_WINDOW_MS;
  return { label, isActiveNow: label === 'Active now', isRecentlyActive };
}
